import { PropsWithChildren } from "react"
import { useTranslation } from "react-i18next"

import { usePaginationParams } from "@/components/pagination/pagination-state"
import { AlertLoadingError } from "@/components/ui/alert"
import { useTableSorting } from "@/hooks/table-sorting"

import { AgentCampaignPicker } from "./agent-campaign-picker"
import { useFetchMissedInboundCalls } from "./missed-calls-api"
import MissedCallsTable from "./missed-calls-table"
import { useMissedCallsSearch } from "./search"

function AgentMissedCalls() {
  const { t } = useTranslation()
  const defaultPageSize = 20
  const { page, pageSize } = usePaginationParams(defaultPageSize)
  const { searchOptions } = useMissedCallsSearch()
  const { campaignIds } = searchOptions
  const sortState = useTableSorting({
    id: "created_at",
    direction: "desc",
  })

  const { data, error, isError, isLoading } = useFetchMissedInboundCalls({
    page,
    pageSize,
    campaignIds,
    sortOptions: sortState.sortOptions,
  })

  if (isError) {
    return (
      <PageLayout>
        <AlertLoadingError
          error={error}
          title={t("missedCalls.dataLoadingError")}
        />
      </PageLayout>
    )
  }

  return (
    <PageLayout>
      <MissedCallsTable
        missedCalls={data?.data.missedInboundCalls ?? []}
        isLoading={isLoading}
        total={data?.data.total ?? 0}
        page={page}
        pageSize={pageSize}
        sortState={sortState}
      />
    </PageLayout>
  )
}

function PageLayout({ children }: PropsWithChildren) {
  return (
    <main className="flex h-full flex-col gap-y-4 px-8 pt-10">
      <div className="container flex flex-col gap-4">
        <PageHeader />
        {children}
      </div>
    </main>
  )
}

function PageHeader() {
  const { t } = useTranslation()
  const { onChange, searchOptions } = useMissedCallsSearch()

  return (
    <div className="flex flex-col gap-y-4">
      <h1 className="font-h2-bold text-neutral-700">
        {t("missedCalls.heading")}
      </h1>
      <div className="font-body-medium-regular text-neutral-600">
        {t("missedCalls.subHeading")}
      </div>
      <div>
        <AgentCampaignPicker
          allowReset
          values={searchOptions.campaignIds}
          onChange={onChange("campaignIds")}
        />
      </div>
    </div>
  )
}

export default AgentMissedCalls
